/******************************************************************************
 * Pure payload / validation helpers for the photo-category admin screen.
 * Kept free of React and the api client so they can be unit-tested directly and
 * reused by both the create (tab) and update (actions) paths.
 *****************************************************************************/

/**
 * buildPhotoCategoryPayload: shape a category form value into the backend
 * PhotoCategoryRequest. Strips translations with an empty label and reshapes each
 * to the `{ language: { id }, label, isDefault }` form the backend expects. Carries
 * the work-order type list, min/max photo counts, and the application field
 * (defaulting to BOTH when absent, matching the backend default).
 * @param data - the category form value
 * @return - the request payload
 */
export function buildPhotoCategoryPayload(data: EasyTech.PhotoCategory): any {
  const translations = (data.translations || [])
    .filter((el: any) => el && el.label && String(el.label).trim().length > 0)
    .map((el: any) => ({
      language: { id: el.language?.id },
      label: el.label,
      isDefault: false
    }));

  return {
    ...(data.id ? { id: data.id } : {}),
    label: data.label,
    enabled: !!data.enabled,
    translations,
    workOrderTypeIds: data.workOrderTypeIds || [],
    minPhotos: data.minPhotos === undefined ? null : data.minPhotos,
    maxPhotos: data.maxPhotos === undefined ? null : data.maxPhotos,
    applicationField: data.applicationField || "BOTH"
  };
}

/**
 * isValidCategory: client-side validity for a photo category. Requires a label;
 * if minPhotos is set it must be >= 0; if maxPhotos is set it must be >= 0 and
 * >= minPhotos (treating an absent min as 0); if applicationField is set it must
 * be one of BEFORE/AFTER/BOTH. Type-list and required-count rules beyond this are
 * enforced by the backend and surfaced as toasts.
 * @param data - the category form value
 * @return - whether the value is submittable
 */
export function isValidCategory(data: Partial<EasyTech.PhotoCategory>): boolean {
  if (!data.label || String(data.label).trim().length === 0) return false;

  const min = data.minPhotos;
  if (min !== null && min !== undefined) {
    if (Number.isNaN(min) || min < 0) return false;
  }

  const max = data.maxPhotos;
  if (max !== null && max !== undefined) {
    if (Number.isNaN(max) || max < 0 || max < (min ?? 0)) return false;
  }

  const application = data.applicationField;
  if (application !== null && application !== undefined) {
    if (!["BEFORE", "AFTER", "BOTH"].includes(application)) return false;
  }

  return true;
}
